import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { api } from '../../api/client'
import { useAdminData } from '../hooks/useAdminData'

interface UserRow {
  id: string
  email: string
  display_name: string
  plan: string  // "founder" | "free" | stripe plan
  plan_status: string
  account_status: string  // "pending" | "active" | "suspended" | "cancelled"
  status_reason?: string | null
  mrr_usd: number
  estimated_cost_usd: number
  month_cost_usd: number
  lifetime_cost_usd: number
  margin: number
  onboarded: boolean
  // s60 — fase actual do pipeline de onboarding. NULL = nunca entrou.
  onboarding_phase?:
    | 'phase1_ingestion'
    | 'phase2_linking'
    | 'phase3_llm_import'
    | 'phase4_summary'
    | 'phase5_chat'
    | 'complete'
    | 'abandoned'
    | null
  last_active_at?: string
  created_at: string
  monthly_budget_usd: number | null
  budget_alert_threshold: number | null
  budget_pct_used: number | null
  budget_state: 'unlimited' | 'ok' | 'soft' | 'hard' | null
}

interface UsersData {
  users: UserRow[]
  total_count: number
  founders_count: number
  paying_count: number
  free_count: number
  cohort: {
    trial_to_paid_rate: number
    avg_conversion_days: number
    monthly_churn_rate: number
    ltv_by_plan: Record<string, number>
  }
  at_risk: UserRow[]
}

const PLAN_STYLES: Record<string, { bg: string; fg: string; label: string }> = {
  founder: { bg: '#fef3c7', fg: '#92400e', label: 'Founder' },
  free: { bg: '#f3f4f6', fg: '#4b5563', label: 'Free' },
  trial: { bg: '#dbeafe', fg: '#1e40af', label: 'Trial' },
  starter: { bg: '#dcfce7', fg: '#166534', label: 'Starter' },
  core: { bg: '#e0e7ff', fg: '#3730a3', label: 'Core' },
  power: { bg: '#fce7f3', fg: '#9f1239', label: 'Power' },
}

function planChip(plan: string) {
  const style = PLAN_STYLES[plan] || { bg: '#f3f4f6', fg: '#4b5563', label: plan }
  return (
    <span
      style={{
        padding: '2px 10px',
        borderRadius: '9999px',
        fontSize: '11px',
        fontWeight: 600,
        fontFamily: 'var(--sans)',
        background: style.bg,
        color: style.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {style.label}
    </span>
  )
}

function BudgetCell({ user, onEdit }: { user: UserRow; onEdit: () => void }) {
  const state = user.budget_state
  if (state === 'unlimited') {
    return (
      <button
        onClick={onEdit}
        style={{
          background: 'transparent',
          border: '1px dashed var(--border)',
          borderRadius: '6px',
          padding: '4px 10px',
          fontFamily: 'var(--sans)',
          fontSize: '11px',
          color: 'var(--ink-muted)',
          cursor: 'pointer',
        }}
      >
        Definir…
      </button>
    )
  }
  const pct = (user.budget_pct_used || 0) * 100
  const barColour =
    state === 'hard' ? '#dc2626' : state === 'soft' ? '#d97706' : 'var(--accent)'
  return (
    <button
      onClick={onEdit}
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'var(--sans)',
        width: '100%',
      }}
    >
      <div style={{ fontSize: '12px', color: 'var(--ink)', marginBottom: '3px' }}>
        ${user.month_cost_usd.toFixed(2)} / ${user.monthly_budget_usd?.toFixed(0)}
      </div>
      <div style={{ height: '4px', background: 'var(--cream-dark)', borderRadius: '2px', overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${Math.min(100, pct)}%`,
            background: barColour,
            transition: 'width 0.3s',
          }}
        />
      </div>
    </button>
  )
}

function BudgetModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserRow
  onClose: () => void
  onSaved: () => void
}) {
  const [limit, setLimit] = useState<string>(
    user.monthly_budget_usd !== null ? String(user.monthly_budget_usd) : '',
  )
  const [threshold, setThreshold] = useState<string>(
    user.budget_alert_threshold !== null ? String(Math.round((user.budget_alert_threshold || 0.8) * 100)) : '80',
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setSaving(true)
    setError(null)
    try {
      const limitNum = limit.trim() === '' ? null : Number(limit)
      if (limitNum !== null && (isNaN(limitNum) || limitNum < 0)) {
        throw new Error('Limite inválido')
      }
      const thrNum = Number(threshold)
      if (isNaN(thrNum) || thrNum <= 0 || thrNum >= 100) {
        throw new Error('Alerta tem de estar entre 1 e 99')
      }
      await api.patch(`/admin/users/${user.id}/budget`, {
        monthly_budget_usd: limitNum,
        budget_alert_threshold: thrNum / 100,
      })
      onSaved()
    } catch (e: any) {
      setError(e.message || 'Falha ao guardar')
      setSaving(false)
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--cream)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          padding: '24px',
          width: '100%',
          maxWidth: '440px',
          fontFamily: 'var(--sans)',
        }}
      >
        <h3 style={{ fontFamily: 'var(--serif)', fontSize: '20px', margin: '0 0 4px 0', color: 'var(--ink)' }}>
          Orçamento — {user.display_name || user.email}
        </h3>
        <p style={{ fontSize: '12px', color: 'var(--ink-muted)', margin: '0 0 20px 0' }}>
          Definir um limite mensal de gasto (LLM + embeddings) para este utilizador.
        </p>

        <label style={{ display: 'block', fontSize: '12px', color: 'var(--ink-muted)', marginBottom: '6px' }}>
          Limite mensal (USD)
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          placeholder="sem limite"
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid var(--border)',
            background: 'white',
            fontSize: '14px',
            color: 'var(--ink)',
            fontFamily: 'var(--sans)',
            outline: 'none',
            marginBottom: '16px',
          }}
        />

        <label style={{ display: 'block', fontSize: '12px', color: 'var(--ink-muted)', marginBottom: '6px' }}>
          Avisar quando atingir (%)
        </label>
        <input
          type="number"
          min="1"
          max="99"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid var(--border)',
            background: 'white',
            fontSize: '14px',
            color: 'var(--ink)',
            fontFamily: 'var(--sans)',
            outline: 'none',
          }}
        />
        <p style={{ fontSize: '11px', color: 'var(--ink-muted)', marginTop: '6px' }}>
          Ao atingir este %, é criado um alerta admin. Ao chegar a 100%, as rotinas e o chat são bloqueados até ao mês seguinte.
        </p>

        {error && (
          <div style={{ marginTop: '12px', fontSize: '12px', color: '#dc2626' }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--ink-light)',
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--ink)',
              color: 'var(--cream)',
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'A guardar...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  )
}

const ACCOUNT_STATUS_STYLES: Record<string, { bg: string; fg: string; label: string }> = {
  pending: { bg: '#fef3c7', fg: '#92400e', label: 'Pendente' },
  active: { bg: '#dcfce7', fg: '#166534', label: 'Activo' },
  suspended: { bg: '#fed7aa', fg: '#9a3412', label: 'Suspenso' },
  cancelled: { bg: '#fecaca', fg: '#991b1b', label: 'Cancelado' },
}

function accountStatusChip(status: string) {
  const s = ACCOUNT_STATUS_STYLES[status] || { bg: '#f3f4f6', fg: '#4b5563', label: status }
  return (
    <span
      style={{
        padding: '2px 10px',
        borderRadius: '9999px',
        fontSize: '11px',
        fontWeight: 600,
        fontFamily: 'var(--sans)',
        background: s.bg,
        color: s.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  )
}

// s60 — onboarding phase badge para a tabela de users.
const ONBOARDING_STYLES: Record<string, { bg: string; fg: string; label: string }> = {
  phase1_ingestion: { bg: '#fef3c7', fg: '#92400e', label: 'Fase 1' },
  phase2_linking: { bg: '#fef3c7', fg: '#92400e', label: 'Fase 2' },
  phase3_llm_import: { bg: '#fef3c7', fg: '#92400e', label: 'Fase 3' },
  phase4_summary: { bg: '#fef3c7', fg: '#92400e', label: 'Fase 4' },
  phase5_chat: { bg: '#dcfce7', fg: '#166534', label: 'A responder' },
  complete: { bg: '#dcfce7', fg: '#166534', label: 'Completo' },
  abandoned: { bg: '#f3f4f6', fg: '#6b7280', label: 'Desistiu' },
}

function onboardingChip(phase: string | null, onboarded: boolean) {
  if (!phase) {
    // user antigo (backfill) ou acabado de entrar que ainda não fez OAuth
    if (onboarded) {
      return (
        <span style={{ padding: '2px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600, background: '#dcfce7', color: '#166534', whiteSpace: 'nowrap' }}>
          Completo
        </span>
      )
    }
    return <span style={{ color: 'var(--ink-muted)', fontSize: '12px' }}>—</span>
  }
  const s = ONBOARDING_STYLES[phase] || { bg: '#f3f4f6', fg: '#4b5563', label: phase }
  return (
    <span style={{ padding: '2px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600, background: s.bg, color: s.fg, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  )
}

export function UsersPage() {
  // URL ?status=pending pre-selects the account-status filter so the KPI
  // card on /admin/overview can deep-link to "only pending users".
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [planFilter, setPlanFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '')
  const [busy, setBusy] = useState<string | null>(null)
  const qs = new URLSearchParams()
  if (planFilter) qs.set('plan', planFilter)
  // Backend has two separate filters: `status` (Stripe plan_status) and
  // `account_status` (moderation). The dropdown filters by account_status.
  if (statusFilter) qs.set('account_status', statusFilter)
  const path = qs.toString() ? `/users?${qs.toString()}` : '/users'
  const { data, loading, refetch } = useAdminData<UsersData>(path, [planFilter, statusFilter])
  const [editingBudget, setEditingBudget] = useState<UserRow | null>(null)

  // Keep URL in sync with the status filter so users can bookmark / share
  // e.g. /admin/utilizadores?status=pending.
  const updateStatusFilter = (next: string) => {
    setStatusFilter(next)
    const nextParams = new URLSearchParams(searchParams)
    if (next) nextParams.set('status', next)
    else nextParams.delete('status')
    setSearchParams(nextParams, { replace: true })
  }

  const moderate = async (
    userId: string,
    action: 'accept' | 'suspend' | 'cancel' | 'reactivate',
  ) => {
    const labels: Record<typeof action, string> = {
      accept: 'Aceitar este utilizador?',
      suspend: 'Suspender este utilizador? Perderá acesso temporariamente.',
      cancel: 'Cancelar este utilizador? Perderá acesso permanentemente (pode ser reactivado).',
      reactivate: 'Reactivar este utilizador?',
    }
    if (!confirm(labels[action])) return

    let reason: string | null = null
    if (action === 'suspend' || action === 'cancel') {
      reason = prompt('Nota (opcional):')
    }

    setBusy(userId)
    try {
      await api.post(`/admin/users/${userId}/${action}`, { reason })
      refetch()
    } catch (e: any) {
      alert(`Erro: ${e.message || 'falhou'}`)
    } finally {
      setBusy(null)
    }
  }

  // Hard-delete path — destructive and irreversible. Two-gate confirmation:
  // 1) native confirm() for the first scare, 2) prompt() to type "DELETE"
  // verbatim. The backend requires the same `?confirm=DELETE` query param
  // so this mirrors the wire contract.
  const destroy = async (user: UserRow) => {
    if (
      !confirm(
        `Apagar PERMANENTEMENTE ${user.email}?\n\n` +
          'Esta acção é IRREVERSÍVEL: remove memórias, vault, conversas, ' +
          'entidades, e a entrada Supabase Auth. O utilizador não pode ' +
          're-login com esta conta.',
      )
    )
      return
    const typed = prompt(
      `Para confirmar, escreve DELETE em maiúsculas (ou cancela):\nA apagar: ${user.email}`,
    )
    if (typed !== 'DELETE') {
      if (typed !== null) alert('Texto não bate certo — nada foi apagado.')
      return
    }

    setBusy(user.id)
    try {
      await api.delete(`/admin/users/${user.id}?confirm=DELETE`)
      refetch()
    } catch (e: any) {
      alert(`Erro a apagar: ${e.message || 'falhou'}`)
    } finally {
      setBusy(null)
    }
  }

  if (loading || !data) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: 'var(--ink-muted)', fontFamily: 'var(--sans)' }}>
        {loading ? 'A carregar...' : 'Sem dados'}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1
          style={{
            fontFamily: 'var(--serif)',
            fontSize: '28px',
            fontWeight: 600,
            color: 'var(--ink)',
            margin: 0,
          }}
        >
          Utilizadores
        </h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={statusFilter}
            onChange={(e) => updateStatusFilter(e.target.value)}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: statusFilter === 'pending' ? '#fef3c7' : 'transparent',
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              color: 'var(--ink-light)',
            }}
            title="Filtrar por estado da conta"
          >
            <option value="">Todas as contas</option>
            <option value="pending">Pendentes</option>
            <option value="active">Activas</option>
            <option value="suspended">Suspensas</option>
            <option value="cancelled">Canceladas</option>
          </select>
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'transparent',
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              color: 'var(--ink-light)',
            }}
          >
            <option value="">Todos os planos</option>
            <option value="founder">Founder</option>
            <option value="free">Free</option>
            <option value="trial">Trial</option>
            <option value="starter">Starter</option>
            <option value="core">Core</option>
            <option value="power">Power</option>
          </select>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <KpiBlock label="Total" value={data.total_count} />
        <KpiBlock label="Founders" value={data.founders_count} accent="#92400e" />
        <KpiBlock label="Pagantes" value={data.paying_count} accent="var(--accent)" />
        <KpiBlock label="Free" value={data.free_count} accent="var(--ink-muted)" />
        <KpiBlock
          label="Churn"
          value={`${(data.cohort.monthly_churn_rate * 100).toFixed(1)}%`}
          accent={data.cohort.monthly_churn_rate > 0.1 ? '#dc2626' : undefined}
        />
        <KpiBlock
          label="Em risco"
          value={data.at_risk.length}
          accent={data.at_risk.length > 0 ? '#d97706' : undefined}
        />
      </div>

      {/* Users table */}
      <div
        style={{
          background: 'rgba(255,255,255,0.8)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          overflow: 'auto',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--sans)', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {['Utilizador', 'Plano', 'Conta', 'Onboarding', 'Status', 'MRR', 'Custo/dia', 'Mês', 'Total', 'Margem', 'Orçamento', 'Último activo', 'Acções'].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: 'var(--ink-muted)',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => {
              const isAtRisk = data.at_risk.some((r) => r.id === u.id)
              const isFounderOrFree = u.plan === 'founder' || u.plan === 'free'

              return (
                <tr
                  key={u.id}
                  onClick={(e) => {
                    // Don't navigate if the click was on a button/input inside the row
                    const target = e.target as HTMLElement
                    if (target.closest('button, input, select, a')) return
                    navigate(`/admin/utilizadores/${u.id}`)
                  }}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    background:
                      u.budget_state === 'hard'
                        ? '#fef2f2'
                        : u.budget_state === 'soft'
                        ? '#fffbeb'
                        : isAtRisk
                        ? '#fffbeb'
                        : 'transparent',
                  }}
                >
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ color: 'var(--ink)', fontWeight: 500 }}>{u.display_name || '—'}</div>
                    <div style={{ color: 'var(--ink-muted)', fontSize: '11px' }}>{u.email}</div>
                  </td>
                  <td style={{ padding: '10px 16px' }}>{planChip(u.plan)}</td>
                  <td style={{ padding: '10px 16px' }}>{accountStatusChip(u.account_status)}</td>
                  <td style={{ padding: '10px 16px' }}>{onboardingChip(u.onboarding_phase ?? null, u.onboarded)}</td>
                  <td
                    style={{
                      padding: '10px 16px',
                      color:
                        u.plan_status === 'active'
                          ? 'var(--accent)'
                          : u.plan_status === 'cancelled'
                          ? '#dc2626'
                          : 'var(--ink-muted)',
                    }}
                  >
                    {u.plan_status === 'none' ? '—' : u.plan_status}
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--ink)' }}>
                    {u.mrr_usd > 0 ? `$${u.mrr_usd.toFixed(0)}` : '—'}
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-light)' }}>
                    ${u.estimated_cost_usd.toFixed(2)}
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-light)' }}>
                    ${u.month_cost_usd.toFixed(2)}
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--ink)', fontWeight: 500 }}>
                    ${u.lifetime_cost_usd.toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: '10px 16px',
                      fontWeight: 600,
                      color: isFounderOrFree
                        ? 'var(--ink-muted)'
                        : u.margin < 0.5
                        ? '#dc2626'
                        : u.margin < 0.65
                        ? '#d97706'
                        : 'var(--accent)',
                    }}
                  >
                    {isFounderOrFree ? '—' : `${(u.margin * 100).toFixed(0)}%`}
                  </td>
                  <td style={{ padding: '10px 16px', minWidth: '120px' }}>
                    <BudgetCell user={u} onEdit={() => setEditingBudget(u)} />
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--ink-muted)', fontSize: '12px' }}>
                    {u.last_active_at ? new Date(u.last_active_at).toLocaleDateString('pt-PT') : 'nunca'}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <ModerationButtons
                      status={u.account_status}
                      disabled={busy === u.id || u.plan === 'founder'}
                      onAction={(a) => moderate(u.id, a)}
                      onDestroy={u.plan === 'founder' ? undefined : () => destroy(u)}
                    />
                  </td>
                </tr>
              )
            })}
            {data.users.length === 0 && (
              <tr>
                <td colSpan={13} style={{ padding: '24px', textAlign: 'center', color: 'var(--ink-muted)' }}>
                  Sem utilizadores
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingBudget && (
        <BudgetModal
          user={editingBudget}
          onClose={() => setEditingBudget(null)}
          onSaved={() => {
            setEditingBudget(null)
            refetch()
          }}
        />
      )}
    </div>
  )
}

function ModerationButtons({
  status,
  disabled,
  onAction,
  onDestroy,
}: {
  status: string
  disabled: boolean
  onAction: (action: 'accept' | 'suspend' | 'cancel' | 'reactivate') => void
  onDestroy?: () => void
}) {
  const btn = (
    label: string,
    action: 'accept' | 'suspend' | 'cancel' | 'reactivate',
    fg: string,
  ) => (
    <button
      onClick={() => onAction(action)}
      disabled={disabled}
      style={{
        background: 'none',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        padding: '4px 10px',
        fontSize: '11px',
        fontFamily: 'var(--sans)',
        fontWeight: 500,
        color: fg,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {label}
    </button>
  )

  // Hard-delete button — shown whenever destroy callback is provided.
  // Distinct red-fill styling so nobody confuses it with soft-cancel.
  const destroyBtn = onDestroy ? (
    <button
      onClick={onDestroy}
      disabled={disabled}
      title="Apagar permanentemente (irreversível)"
      style={{
        background: '#fee2e2',
        border: '1px solid #fecaca',
        borderRadius: '6px',
        padding: '4px 10px',
        fontSize: '11px',
        fontFamily: 'var(--sans)',
        fontWeight: 600,
        color: '#991b1b',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      Apagar
    </button>
  ) : null

  if (status === 'pending') {
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        {btn('Aceitar', 'accept', 'var(--accent)')}
        {btn('Rejeitar', 'cancel', '#dc2626')}
        {destroyBtn}
      </div>
    )
  }
  if (status === 'active') {
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        {btn('Suspender', 'suspend', '#d97706')}
        {btn('Cancelar', 'cancel', '#dc2626')}
        {destroyBtn}
      </div>
    )
  }
  if (status === 'suspended') {
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        {btn('Reactivar', 'reactivate', 'var(--accent)')}
        {btn('Cancelar', 'cancel', '#dc2626')}
        {destroyBtn}
      </div>
    )
  }
  if (status === 'cancelled') {
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        {btn('Reactivar', 'reactivate', 'var(--accent)')}
        {destroyBtn}
      </div>
    )
  }
  return <span style={{ color: 'var(--ink-muted)', fontSize: '12px' }}>—</span>
}


function KpiBlock({
  label,
  value,
  accent,
}: {
  label: string
  value: string | number
  accent?: string
}) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.8)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '16px 20px',
        flex: 1,
        minWidth: '120px',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--sans)',
          fontSize: '11px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--ink-muted)',
          marginBottom: '4px',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--serif)',
          fontSize: '24px',
          fontWeight: 600,
          color: accent || 'var(--ink)',
        }}
      >
        {value}
      </div>
    </div>
  )
}
